import { FilterParams } from '../redux/api/savedViews/savedViews.model';
import {
  InvoiceService,
  InvoiceServiceUnit
} from '../redux/api/invoiceService/invoiceService.model';
import { CreateLoadConfig, CreateLoadFields, LoadDocumentType } from '../redux/api/load/load.model';
import {
  ORDER_STATUS,
  Order,
  OrderRate,
  OrderSearchFilterParams,
  SearchOrdersResponse
} from '../redux/api/orders/orders.model';
import {
  StopStatus,
  Trip,
  TripSearchFilterParams,
  TRIP_STATUS,
  TripStop
} from '../redux/api/trips/trips.model';
import NumberUtils from './numberUtils';

class LoadUtils {
  /**
   * Returns an array of load required fields for a status
   * @param status
   * @param config
   * @returns
   */
  static getRequiredFields = (
    status: ORDER_STATUS,
    config: CreateLoadConfig[]
  ): CreateLoadFields[] => {
    let requiredFields: CreateLoadFields[] = [];

    const statusConfig = config.filter((config) => {
      return config.status === status;
    });

    statusConfig.map((s) => {
      requiredFields = s.required_fields;
    });

    return requiredFields;
  };

  /**
   * Clears a specific param from the filter params
   * @param prevParams Current filter params
   * @param paramName Name of the filter params property to delete
   * @returns Updated filter params
   */
  static clearFromFilter = (
    prevParams: FilterParams | undefined,
    paramToReset: keyof FilterParams
  ): FilterParams => {
    const state = { ...prevParams };
    delete state[paramToReset];
    return state;
  };

  /**
   * Clears a specific param from the filter params
   * @param prevParams Current filter params
   * @param paramName Name of the filter params property to delete
   * @returns Updated filter params
   */
  static clearFromFilterArray = (
    prevParams: FilterParams | undefined,
    paramsToReset: (keyof FilterParams)[]
  ): FilterParams => {
    const state = { ...prevParams };
    paramsToReset.forEach((param) => delete state[param]);
    return state;
  };

  /**
   * Clears a specific param from the Order filter params
   * @deprecated Use clearFromFilter instead for DispatchViews
   */
  static clearFromOrderFilter = (
    prevParams: OrderSearchFilterParams | undefined,
    paramToReset: keyof OrderSearchFilterParams
  ): OrderSearchFilterParams => {
    const state = { ...prevParams };
    delete state[paramToReset];
    return state;
  };

  /**
   * Clears a specific param from the Trip filter params
   * @deprecated Use clearFromFilter instead for DispatchViews
   */
  static clearFromTripFilter = (
    prevParams: TripSearchFilterParams | undefined,
    paramToReset: keyof TripSearchFilterParams
  ): TripSearchFilterParams => {
    const state = { ...prevParams };
    delete state[paramToReset];
    return state;
  };

  /**
   * Checks if BOL or POD is missing regardless of load status
   * @param load
   */
  static isDriverDocumentMissing = (load: Order): boolean => {
    let isDriverDocumentMissing = true;
    load?.order_attachments?.map((doc) => {
      if ([LoadDocumentType.billOfLading, LoadDocumentType.proofOfDelivery].includes(doc.type))
        isDriverDocumentMissing = false;

      // any document uploaded by driver is considered BOL
      if (doc.driver_id) isDriverDocumentMissing = false;
    });
    return isDriverDocumentMissing;
  };

  /**
   * Formats load rate
   * @param rate load rate to clean param
   */
  static formatRate = (rate: string | number | undefined | null): string => {
    if (!rate) return '';
    const rateNumber = Number(rate);
    return NumberUtils.getCurrency(rateNumber, 4);
  };

  /**
   * returns true if even one of the passed load has different customer
   * @param loads
   */
  static hasDifferentCustomer = (loads: SearchOrdersResponse[]): boolean => {
    if (loads.length == 0) return true;

    const primaryClientId = loads[0].primary_client_id;
    return !loads.every((load) => load.primary_client_id === primaryClientId);
  };

  /**
   * returns readable_id of any load that has invoice from passed loads
   * @param loads
   */
  static findLoadWithInvoice = (loads: SearchOrdersResponse[]): string | number | undefined => {
    const loadWithInvoice = loads.find((load) => load.has_invoice);
    return loadWithInvoice ? loadWithInvoice.readable_id : undefined;
  };

  /**
   * returns all load stops for a given load
   */
  static getAllStops = (load: any, isLtl = false): TripStop[] => {
    if (!load) return [];
    if (!isLtl && load.trips) {
      const segments = load.trips;
      const stops: TripStop[] = [];
      segments?.forEach((segment: any, index: any) => {
        if (index == 0) {
          stops.push(...segment.stops);
        } else {
          // exclude first stop of each segment after first segment
          stops.push(...segment.stops.slice(1));
        }
      });
      return stops;
    } else {
      const existingStops =
        load.stops && load.stops.length > 0 ? load.stops : load.full_route_stop_overview;

      return existingStops;
    }
  };

  /**
   * returns next stop number that needs to be marked
   * Eg. if first stop is not started, it will return 1, if first stop is completed and second stop is not completed, it will return 2
   */
  static getNextStopNumber = (stops: TripStop[]): number | undefined => {
    const currentStop = stops.find((stop) => stop.status !== StopStatus.complete);

    if (!currentStop) return;
    return currentStop.sort_order;
  };

  /**
   * return next stop status for a given stops array
   * Eg. if first stop is not started, it will return 'en route', if first stop is completed and second stop is in In transit, it will return 'complete'
   */
  static getNextStopStatus = (stops: TripStop[]): [TripStop, string] | undefined => {
    const currentStop = stops.find((stop) => stop.status !== StopStatus.complete);
    if (currentStop) {
      const allStopStatus = Object.values(StopStatus).filter((item) => {
        return isNaN(Number(item));
      });
      const currStopStatusIndex = allStopStatus.indexOf(
        currentStop.status || StopStatus.notStarted
      );

      const nextStopStatusIndex =
        currStopStatusIndex + 1 > allStopStatus.length - 1
          ? currStopStatusIndex
          : currStopStatusIndex + 1;
      return [currentStop, allStopStatus[nextStopStatusIndex]];
    }
  };

  /**
   * returns readable text to show for a given stop status in public link
   * @param stopStatus
   */
  static getStopStatusReadableText = (stopStatus: StopStatus): string => {
    switch (stopStatus) {
      case StopStatus.notStarted:
        return 'not started';
      case StopStatus.inTransit:
        return 'en route';
      case StopStatus.arrived:
        return 'arrived';
      case StopStatus.complete:
        return 'completed';
      default:
        return '';
    }
  };

  /** returns readable text to show for a given stop status
   * @param stopStatus
   */
  static getStopStatusReadableLabel = (stopStatus: StopStatus): string => {
    switch (stopStatus) {
      case StopStatus.notStarted:
        return 'Not Started';
      case StopStatus.inTransit:
        return 'In-Transit';
      case StopStatus.arrived:
        return 'Arrived';
      case StopStatus.complete:
        return 'Completed';
      default:
        return '';
    }
  };

  /**
   * returns readable text for a Trip status
   */
  static getTripStatusReadableText = (status: TRIP_STATUS): string => {
    switch (status) {
      case TRIP_STATUS.CANCELLED:
        return 'Cancelled';
      case TRIP_STATUS.DRAFT:
        return 'Draft';
      case TRIP_STATUS.DISPATCHED:
        return 'Dispatched';
      case TRIP_STATUS.IN_TRANSIT:
        return 'In-Transit';
      case TRIP_STATUS.DELIVERED:
        return 'Delivered';
      default:
        return '';
    }
  };

  /**
   * returns next stop level status label for a given stops array. Format is 'Stop {stopNumber}: Mark as {stopStatus}'
   * Used in public link's segment to show next stop status in action button
   * @param stops[]
   * @param stopStartOffse offset to start stop number from
   */
  static getNextStopLevelStatusLabel = (
    stops: TripStop[],
    stopStartOffset = 0
  ): string | undefined => {
    const nextStopNumber = this.getNextStopNumber(stops);
    if (!nextStopNumber) return;

    const nextStopInfo = this.getNextStopStatus(stops);
    if (!nextStopInfo) return;

    const [, nextStopStatus] = nextStopInfo;

    if ((nextStopStatus as StopStatus) === StopStatus.complete) {
      return `Mark 'Stop ${nextStopNumber + stopStartOffset}' as ${this.getStopStatusReadableText(
        nextStopStatus as StopStatus
      )}`;
    }

    return `Mark as ${this.getStopStatusReadableText(nextStopStatus as StopStatus)} to 'Stop ${
      nextStopNumber + stopStartOffset
    }'`;
  };

  /**
   * returns next stop label for a given segment. Format is 'Stop {stopNumber}: Mark as {stopStatus}'
   * Used in public link's segment to show next stop status in action button
   * @param segment
   */
  static getNextStopLevelStatusForSegment = (segment: Trip): string | undefined => {
    const stops = segment.stops;
    return this.getNextStopLevelStatusLabel(stops);
  };

  /**
   * return simple rate converted to advanced rate with default service selected for the rate
   */
  static createDefaultAdvancedRate = (
    simpleRate: number | undefined,
    quickbooksEnabled: boolean,
    invoiceServices: InvoiceService[] | undefined,
    makeRateOptional: boolean
  ): OrderRate[] => {
    const orderRates: OrderRate[] = [];
    const defaultService = invoiceServices?.find((service) => service.isDefault);

    if (!(makeRateOptional && !simpleRate)) {
      orderRates.push({
        invoice_service_id: !quickbooksEnabled ? defaultService?.id : undefined,
        qbo_service_id: quickbooksEnabled ? defaultService?.id : undefined,
        qbo_service_type: quickbooksEnabled ? defaultService?.type : undefined,
        service_name: defaultService?.name || '',
        unit: defaultService?.unit || InvoiceServiceUnit.FLAT,
        rate: simpleRate || defaultService?.default_rate || 0,
        quantity: 1,
        sort_order: 1
      });
    }

    return orderRates;
  };

  /**
   * return total number of stops before current trip
   */
  static getPreviousStopsCount = (trips: Trip[], currentTripIndex: number): number => {
    const totalStopsBefore = trips.slice(0, currentTripIndex).reduce((acc, curr) => {
      return acc + curr.sort_order === 0 ? curr.stops.length : curr.stops.length - 1;
    }, 0);
    return totalStopsBefore;
  };

  /**
   * return total number of stops before current trip given all stops
   */
  static getPreviousStopsCountForTrip = (allStops: TripStop[], tripId: string): number => {
    let count = 0;

    while (allStops[count].trip_id !== tripId) {
      count++;
    }

    return count;
  };
}

export default LoadUtils;
